import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useApiMutation, useApiQuery } from '@/shared/hooks/useApi';
import { api } from '@/shared/services/api';
import { ApiResponse } from '@/shared/types/apiTypes';

import {
  ApprovalStatus,
  DownloadFileRequest,
  DownloadFileResponse,
  PendingApprovalListParams,
  PendingApprovalListResponse,
  UpdateApprovalStatusRequest,
} from '../types/userTypes';

const ITEMS_PER_PAGE = 20;

const fetchPendingApprovalList = async (
  params: PendingApprovalListParams,
): Promise<ApiResponse<PendingApprovalListResponse>> => {
  const searchParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    filter: params.filter,
  });

  if (params.keyword && params.keyword.trim()) {
    searchParams.append('keyword', params.keyword.trim());
  }

  return api.get<PendingApprovalListResponse>(
    `/api/v1/approval/list?${searchParams.toString()}`,
  );
};

const updateApprovalStatus = async (
  request: UpdateApprovalStatusRequest,
): Promise<ApiResponse<null>> => {
  return api.patch<null>('/api/v1/approval', request);
};

const downloadFile = async (
  request: DownloadFileRequest,
): Promise<ApiResponse<DownloadFileResponse>> => {
  return api.post<DownloadFileResponse>('/api/v1/s3/download', request);
};

export const usePendingApproval = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [filter, setFilter] = useState<ApprovalStatus | 'ALL'>('ALL');
  const [shouldReset, setShouldReset] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const pageParam = searchParams.get('page');
    const keywordParam = searchParams.get('keyword');
    const filterParam = searchParams.get('filter') as ApprovalStatus | 'ALL';

    const page = pageParam ? parseInt(pageParam, 10) : 1;
    const searchKeyword = keywordParam || '';
    const statusFilter = filterParam || 'ALL';

    setCurrentPage(page);
    setKeyword(searchKeyword);
    setFilter(statusFilter);

    if (!pageParam) {
      searchParams.set('page', '1');
      if (!filterParam) {
        searchParams.set('filter', 'ALL');
      }
      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    }
  }, [location, navigate]);

  const {
    data: pendingApprovalData,
    isLoading,
    error,
    refetch,
  } = useApiQuery(
    ['pendingApprovalList', currentPage, keyword, filter],
    () =>
      fetchPendingApprovalList({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        keyword,
        filter,
      }),
    {
      enabled: true,
    },
  );

  const updateStatusMutation = useApiMutation(updateApprovalStatus, {
    onError: (err: any) => {
      alert(err?.message || 'Failed to update approval status.');
    },
  });

  const downloadFileMutation = useApiMutation(downloadFile, {
    onSuccess: (data) => {
      if (data?.data?.url) {
        window.open(data.data.url, '_blank');
      }
    },
    onError: (err: any) => {
      alert(err?.message || 'Failed to download file.');
    },
  });

  const updateURL = useCallback(
    (
      page: number,
      searchKeyword: string,
      statusFilter: ApprovalStatus | 'ALL',
    ) => {
      const searchParams = new URLSearchParams();
      searchParams.set('page', page.toString());
      searchParams.set('filter', statusFilter);

      if (searchKeyword && searchKeyword.trim()) {
        searchParams.set('keyword', searchKeyword.trim());
      }

      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    },
    [location.pathname, navigate],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      updateURL(page, keyword, filter);
    },
    [keyword, filter, updateURL],
  );

  const handleSearch = useCallback(
    (searchTerm: string) => {
      setKeyword(searchTerm);
      setCurrentPage(1);
      updateURL(1, searchTerm, filter);
    },
    [filter, updateURL],
  );

  const handleFilterChange = useCallback(
    (statusFilter: ApprovalStatus | 'ALL') => {
      setFilter(statusFilter);
      setCurrentPage(1);
      updateURL(1, keyword, statusFilter);
    },
    [keyword, updateURL],
  );

  const handleReset = useCallback(() => {
    setKeyword('');
    setFilter('ALL');
    setCurrentPage(1);
    setShouldReset(true);
    updateURL(1, '', 'ALL');
  }, [updateURL]);

  const handleResetComplete = useCallback(() => {
    setShouldReset(false);
  }, []);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleStatusUpdate = useCallback(
    (uuid: string, approvalStatus: ApprovalStatus, onSuccess?: () => void) => {
      updateStatusMutation.mutate(
        { uuid, approvalStatus },
        {
          onSuccess: () => {
            refetch();
            if (onSuccess) {
              onSuccess();
            }
          },
        },
      );
    },
    [updateStatusMutation, refetch],
  );

  const handleFileDownload = useCallback(
    (imageUrl: string) => {
      downloadFileMutation.mutate({ imageUrl });
    },
    [downloadFileMutation],
  );

  const pendingUsers = pendingApprovalData?.data?.userList || [];
  const totalCount = pendingApprovalData?.data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const hasError = !!error;
  const isEmpty = !isLoading && !hasError && pendingUsers.length === 0;

  const showError = useCallback((errorMessage: string) => {
    alert(errorMessage);
  }, []);

  useEffect(() => {
    if (hasError && error) {
      showError(error.message);
    }
  }, [hasError, error, showError]);

  return {
    pendingUsers,
    currentPage,
    totalPages,
    keyword,
    filter,
    isLoading,
    hasError,
    isEmpty,
    shouldReset,
    totalCount,

    handlePageChange,
    handleSearch,
    handleFilterChange,
    handleReset,
    handleResetComplete,
    handleRefresh,
    handleStatusUpdate,
    handleFileDownload,

    isUpdatingStatus: updateStatusMutation.isPending,
    isDownloadingFile: downloadFileMutation.isPending,
    error,
  };
};
